import { cloneElement, isValidElement, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@folio/components/ui/alert/alert';
import { Card, CardContent } from '@folio/components/ui/card/card';
import {
  getRoutesFromStepper,
  StepperFooter,
  useStepperContext,
  ValidatedStepper,
} from '@folio/components';
import { useBrokerageFirmDocumentsValidationRule } from '@folio/hooks';
import {
  AssetFunnelModule,
  BillingAssignmentFunnelModule,
  BrokerageFirmUploadDocumentsModule,
  CompanyFunnelModule,
  ComplianceFunnelModule,
  CustomerFunnelModule,
  DistributionRequestFunnelModule,
  DocumentUploadFunnelModule,
  DynamicStepsModule,
  EmptyAssetFunnelModule,
  FleetAssetFunnelModule,
  FunnelEndModule,
  IntermediaryFunnelModule,
  IntermediarySignatureFunnelModule,
  MtaCreationFunnelModule,
  PolicyFunnelModule,
  IntermediaryFunnelEndModule,
  LegacyQuoteFunnelModule,
  BillingSelectionFunnelModule,
  PaymentMethodFunnelModule,
  type AfterSubmitFn,
} from '@folio/modules';
import type { Step } from '@folio/types';
import { FeatureFlagRoute } from './FeatureFlagRoute';
import i18next from 'i18next';
import { policyControllerPushPolicyEvent } from 'korint-sdk';
import type {
  AfterSubmitResult,
  CustomFunnelConfig,
  FunnelStep,
  KorintModulesProductsConfig,
  UniqAppConfig,
} from 'korint-sdk/react';
import { CustomFunnelAuth } from 'korint-sdk/react';
import { AnonymousLandingPage } from './AnonymousLandingPage';
import { Stack } from '@folio/components/ui/flex';
import { useParams } from 'react-router-dom';

type BeforeNextFn = (params: {
  policyId: string;
  branchId: string;
}) => AfterSubmitResult | Promise<AfterSubmitResult>;

type ModuleStepNextButton = { useEnabled?: () => boolean };

const useAlwaysEnabled = () => true;

function ModuleStepFooter({
  nextButtonText,
  nextButton,
  beforeNext,
}: {
  nextButtonText?: string;
  nextButton?: ModuleStepNextButton;
  beforeNext?: BeforeNextFn;
}) {
  const stepper = useStepperContext();
  const { policyId, branchId } = useParams<{ policyId: string; branchId: string }>();
  const [isPending, setIsPending] = useState(false);
  const useEnabled = nextButton?.useEnabled ?? useAlwaysEnabled;
  const isEnabled = useEnabled();

  const handleNext = async () => {
    if (!beforeNext || !policyId || !branchId) return stepper.next();
    setIsPending(true);
    try {
      const result = await beforeNext({ policyId, branchId });
      stepper.next(result?.branchId ? { branchId: result.branchId } : undefined);
    } finally {
      setIsPending(false);
    }
  };

  return (
    <StepperFooter
      nextButtonEnabled={isEnabled}
      onNext={handleNext}
      loading={isPending}
      nextButtonText={nextButtonText}
    />
  );
}

/**
 * Use this wrapper to display a module in a funnel
 * @param module - The module to wrap
 * @returns The module with a next button
 */
function ModuleWrapper({
  module,
  alert,
  nextButtonText,
  helper,
  nextButton,
  beforeNext,
}: {
  module: React.ReactNode;
  alert?: {
    variant?: 'default' | 'info' | 'destructive';
    className?: string;
    icon?: React.ReactNode;
    title?: string;
    description: React.ReactNode;
  };
  nextButtonText?: string;
  helper?: React.ReactNode;
  nextButton?: ModuleStepNextButton;
  beforeNext?: BeforeNextFn;
}) {
  const content = (
    <Stack>
      {alert && (
        <Alert variant={alert.variant} className={alert.className}>
          {alert.icon}
          <div>
            {alert.title && <AlertTitle>{alert.title}</AlertTitle>}
            <AlertDescription>{alert.description}</AlertDescription>
          </div>
        </Alert>
      )}
      {module}
    </Stack>
  );

  return (
    <Stack>
      {withHelper(content, helper)}
      <ModuleStepFooter
        nextButtonText={nextButtonText}
        nextButton={nextButton}
        beforeNext={beforeNext}
      />
    </Stack>
  );
}

function buildAfterSubmitWithTriggerEvent(
  originalAfterSubmit: AfterSubmitFn | undefined,
  triggerEvent: string | undefined,
  triggerAlways: boolean | undefined,
): AfterSubmitFn | undefined {
  if (!triggerEvent) return originalAfterSubmit;

  return async (data, params) => {
    if (params.isDirty || triggerAlways) {
      await policyControllerPushPolicyEvent(
        params.policyId,
        { eventType: triggerEvent },
        { branchId: params.branchId },
      );
    }
    return originalAfterSubmit?.(data, params);
  };
}

function withHelper(element: React.ReactNode, helper: React.ReactNode): React.ReactNode {
  if (!helper) return element;
  return (
    <Stack className="xl:flex-row">
      <div className="lg:w-full xl:w-5/7">{element}</div>
      <div className="lg:w-full xl:w-2/7">
        <Card className="sticky top-4">
          <CardContent className="pt-6">{helper}</CardContent>
        </Card>
      </div>
    </Stack>
  );
}

type FunnelDisplayOptions = {
  productId?: string;
  displayContext?: 'broker' | 'policyholder';
};

function mapConfigToStep(step: FunnelStep, options: FunnelDisplayOptions = {}): Step {
  const mapped = buildStepFromConfig(step, options);
  // Force a remount per step. Consecutive steps of the same type render the same component type
  // and React reuses the instance otherwise (no remount), so the unmount-save (persist) and the
  // useQuery refetch never fire when navigating between them — saved data then looks empty on return.
  return isValidElement(mapped.element)
    ? { ...mapped, element: cloneElement(mapped.element, { key: step.path }) }
    : mapped;
}

function buildStepFromConfig(
  step: FunnelStep,
  { productId, displayContext = 'broker' }: FunnelDisplayOptions,
): Step {
  const common = { path: step.path, hidden: step.hidden, icon: step.icon };
  const h = step.helper;
  if (step.type === 'custom') {
    return { ...common, element: withHelper(step.component, h) } as Step;
  }
  if (step.type === 'module') {
    return {
      ...common,
      element: (
        <ModuleWrapper
          module={step.module}
          alert={step.alert}
          nextButtonText={step.nextButtonText}
          helper={h}
          nextButton={step.nextButton}
          beforeNext={step.beforeNext}
        />
      ),
    } as Step;
  }
  if (step.type === 'complianceFunnel') {
    return { ...common, element: <ComplianceFunnelModule helper={h} /> } as Step;
  }
  if (step.type === 'policyFunnel') {
    return {
      ...common,
      element: (
        <PolicyFunnelModule
          sections={step.sections}
          path={step.path}
          afterSubmit={step.afterSubmit}
          helper={h}
        />
      ),
    } as Step;
  }
  if (step.type === 'dynamicSteps') {
    return { ...common, isDynamic: true, element: <DynamicStepsModule /> } as Step;
  }
  if (step.type === 'customerFunnel') {
    const afterSubmit = buildAfterSubmitWithTriggerEvent(
      step.afterSubmit,
      step.triggerEvent,
      step.triggerAlways,
    );
    return {
      ...common,
      element: (
        <CustomerFunnelModule
          sections={step.sections}
          path={step.path}
          maxCustomers={step.maxCustomers}
          minCustomers={step.minCustomers}
          description={step.description}
          role={step.role}
          addCustomerButtonLabel={step.addCustomerButtonLabel}
          afterSubmit={afterSubmit}
          helper={h}
        />
      ),
    } as Step;
  }
  if (step.type === 'companyFunnel') {
    return {
      ...common,
      element: withHelper(
        <CompanyFunnelModule
          additionalFields={step.additionalFields}
          additionalSections={step.additionalSections}
          afterSubmit={step.afterSubmit}
        />,
        h,
      ),
    } as Step;
  }
  if (step.type === 'newMTACreation') {
    return {
      ...common,
      element: withHelper(<MtaCreationFunnelModule productId={productId ?? ''} />, h),
    } as Step;
  }
  if (step.type === 'quoteFunnel') {
    return {
      ...common,
      element: (
        <LegacyQuoteFunnelModule
          policySections={step.policySections}
          policySectionsAfterOptions={step.policySectionsAfterOptions}
          assetSections={step.assetSections}
          manualQuoteSections={step.manualQuoteSections}
          additionalQuoteComponentsSections={step.additionalQuoteComponentsSections}
          nonDelegatedDocuments={step.nonDelegatedDocuments}
          showQuoteRecap={step.showQuoteRecap}
          additionalComponent={step.additionalComponent}
          additionalComponentAfterOptions={step.additionalComponentAfterOptions}
          helper={h}
        />
      ),
    } as Step;
  }

  if (step.type === 'intermediaryFunnel') {
    return {
      ...common,
      element: withHelper(<IntermediaryFunnelModule path={step.path} fields={step.fields} />, h),
    } as Step;
  }
  if (step.type === 'distributionRequestFunnel') {
    return {
      ...common,
      element: withHelper(<DistributionRequestFunnelModule />, h),
    } as Step;
  }

  if (step.type === 'assetFunnel') {
    return {
      ...common,
      element: (
        <AssetFunnelModule
          fields={step.fields}
          sections={step.sections}
          minAssets={step.minAssets}
          maxAssets={step.maxAssets}
          path={step.path}
          description={step.description}
          showScrollNav={step.showScrollNav}
          afterSubmit={step.afterSubmit}
          helper={h}
        />
      ),
    } as Step;
  }

  if (step.type === 'fleetAssetFunnel') {
    return {
      ...common,
      element: (
        <FleetAssetFunnelModule
          fields={step.fields}
          path={step.path}
          description={step.description}
          helper={h}
        />
      ),
    } as Step;
  }

  if (step.type === 'emptyAssetFunnel') {
    return { ...common, element: <EmptyAssetFunnelModule />, hidden: true } as Step;
  }

  if (step.type === 'funnelEnd') {
    return {
      ...common,
      element: (
        <FunnelEndModule
          id={step.id}
          additionalComponents={step.additionalComponents}
          callback={step.callback}
          redirectTo={step.redirectTo}
        />
      ),
    };
  }

  if (step.type === 'intermediarySignatureFunnel') {
    return {
      ...common,
      element: withHelper(
        <IntermediarySignatureFunnelModule displayContext={step.displayContext} />,
        h,
      ),
    } as Step;
  }

  if (step.type === 'intermediaryFunnelEnd') {
    return {
      ...common,
      element: <IntermediaryFunnelEndModule />,
    } as Step;
  }

  if (step.type === 'billingAssignmentFunnel') {
    return {
      ...common,
      element: withHelper(<BillingAssignmentFunnelModule roleIcons={step.roleIcons} />, h),
    } as Step;
  }

  if (step.type === 'billingSelectionFunnel') {
    return {
      ...common,
      element: (
        <BillingSelectionFunnelModule
          frequencyIcons={step.frequencyIcons}
          earlyPaymentDisplay={step.earlyPaymentDisplay}
          helper={h}
        />
      ),
    } as Step;
  }

  if (step.type === 'paymentMethodFunnel') {
    return { ...common, element: withHelper(<PaymentMethodFunnelModule />, h) } as Step;
  }

  if (step.type === 'documentUploadFunnel') {
    return {
      ...common,
      element: (
        <DocumentUploadFunnelModule
          context={step.context}
          displayContext={displayContext}
          helper={h}
        />
      ),
    } as Step;
  }

  if (step.type === 'brokerageFirmUploadDocumentsFunnelValidated') {
    return { ...common, element: <BrokerageFirmUploadDocumentsFunnelValidated /> } as Step;
  }

  // This should never happen due to exhaustive type checking
  const _exhaustiveCheck: never = step;
  return _exhaustiveCheck;
}

function BrokerageFirmUploadDocumentsFunnelValidated() {
  const rule = useBrokerageFirmDocumentsValidationRule();
  return (
    <Stack>
      <BrokerageFirmUploadDocumentsModule />
      <ValidatedStepper rules={[rule]} />
    </Stack>
  );
}

function isNonEmptyArray<T>(arr: T[]): arr is [T, ...T[]] {
  return arr.length > 0;
}

export function computeIntermediaryRoutesForStepper(config: UniqAppConfig) {
  const intermediarySteps =
    config.intermediaryFunnelSteps?.steps.map(step => mapConfigToStep(step)) ?? [];

  const intermediaryRoutes = isNonEmptyArray(intermediarySteps)
    ? [
        ...getRoutesFromStepper({
          steps: intermediarySteps,
          basePath: '/:parentBrokerageFirmId/brokerage-firm/:brokerageFirmId?',
          afterEndPath: config.intermediaryFunnelSteps?.afterEndPath,
          stepsI18nPrefix: 'stepper.distributionRequest',
        }),
      ]
    : [];
  return intermediaryRoutes;
}

export function computePolicyholderRoutesForStepper(
  productId: string,
  config: KorintModulesProductsConfig,
) {
  const title = i18next.t(`products.${productId}.label`);
  const discoverySteps =
    config.policyholderDiscoveryFunnelSteps?.steps.map(step =>
      mapConfigToStep(step, { productId, displayContext: 'policyholder' }),
    ) ?? [];
  const mtaSteps =
    config.policyholderMtaFunnelSteps?.steps.map(step =>
      mapConfigToStep(step, { productId, displayContext: 'policyholder' }),
    ) ?? [];
  const renewalSteps =
    config.policyholderRenewalFunnelSteps?.steps.map(step =>
      mapConfigToStep(step, { productId, displayContext: 'policyholder' }),
    ) ?? [];

  const stepsI18nPrefix = `stepper.${productId}`;

  const discoveryRoutes = isNonEmptyArray(discoverySteps)
    ? getRoutesFromStepper({
        steps: discoverySteps,
        basePath: `/policyholder${config.productCustomPath}/:policyId/:branchId?/final`,
        afterEndPath: config.policyholderDiscoveryFunnelSteps?.afterEndPath,
        title,
        stepsI18nPrefix,
      })
    : [];

  const mtaRoutes = isNonEmptyArray(mtaSteps)
    ? getRoutesFromStepper({
        steps: mtaSteps,
        basePath: `/policyholder${config.productCustomPath}/:policyId/:branchId?/mta`,
        afterEndPath: config.policyholderMtaFunnelSteps?.afterEndPath,
        title,
        stepsI18nPrefix,
      })
    : [];

  const renewalRoutes = isNonEmptyArray(renewalSteps)
    ? getRoutesFromStepper({
        steps: renewalSteps,
        basePath: `/policyholder${config.productCustomPath}/:customerId/:policyId/:branchId?/renewal`,
        afterEndPath: config.renewalFunnelSteps?.afterEndPath,
        title,
        stepsI18nPrefix,
      })
    : [];

  return [...discoveryRoutes, ...mtaRoutes, ...renewalRoutes];
}

export function computeRoutesForStepper(productId: string, config: KorintModulesProductsConfig) {
  const title = i18next.t(`products.${productId}.label`);
  const stepsI18nPrefix = `stepper.${productId}`;
  const discoverySteps =
    config.discoveryFunnelSteps?.steps.map(step => mapConfigToStep(step, { productId })) ?? [];
  const mtaSteps =
    config.mtaFunnelSteps?.steps.map(step => mapConfigToStep(step, { productId })) ?? [];
  const renewalSteps =
    config.renewalFunnelSteps?.steps.map(step => mapConfigToStep(step, { productId })) ?? [];

  const quoteDerogationSteps =
    config.policyDashboardEventsModuleConfig?.quoteDerogationFunnelModule?.steps.map(step =>
      mapConfigToStep(step, { productId }),
    ) ?? [];

  const discoveryRoutes = isNonEmptyArray(discoverySteps)
    ? getRoutesFromStepper({
        steps: discoverySteps,
        basePath: `/discovery${config.productCustomPath}/:customerId/:policyId/:branchId?`,
        afterEndPath: config.discoveryFunnelSteps?.afterEndPath,
        title,
        stepsI18nPrefix,
      })
    : [];

  const mtaRoutes = isNonEmptyArray(mtaSteps)
    ? getRoutesFromStepper({
        steps: mtaSteps,
        basePath: `/mta${config.productCustomPath}/:customerId/:policyId/:branchId?`,
        afterEndPath: config.mtaFunnelSteps?.afterEndPath,
        title,
        stepsI18nPrefix,
      })
    : [];

  const renewalRoutes = isNonEmptyArray(renewalSteps)
    ? getRoutesFromStepper({
        steps: renewalSteps,
        basePath: `/renewal${config.productCustomPath}/:customerId/:policyId/:branchId?`,
        afterEndPath: config.renewalFunnelSteps?.afterEndPath,
        title,
        stepsI18nPrefix,
      })
    : [];

  const quoteDerogationRoutes = isNonEmptyArray(quoteDerogationSteps)
    ? getRoutesFromStepper({
        steps: quoteDerogationSteps,
        basePath: `/quote-derogation${config.productCustomPath}/:customerId/:policyId/:branchId?`,
        afterEndPath:
          config.policyDashboardEventsModuleConfig?.quoteDerogationFunnelModule?.afterEndPath,
        title,
        stepsI18nPrefix,
      })
    : [];

  return [...discoveryRoutes, ...mtaRoutes, ...quoteDerogationRoutes, ...renewalRoutes];
}

function withFeatureFlag<Route extends { element?: React.ReactNode }>(
  routes: Route[],
  flag: CustomFunnelConfig['featureFlag'],
  productId?: string,
): Route[] {
  if (!flag) {
    return routes;
  }

  return routes.map(route => ({
    ...route,
    element: (
      <FeatureFlagRoute flag={flag} productId={productId}>
        {route.element}
      </FeatureFlagRoute>
    ),
  }));
}

export function computeCustomFunnelRoutes(config: UniqAppConfig) {
  const customFunnels = config.customFunnels ?? [];

  return customFunnels.map(funnel => {
    const steps = funnel.steps.map(step =>
      mapConfigToStep(step, {
        displayContext: funnel.auth === CustomFunnelAuth.ANONYMOUS ? 'policyholder' : 'broker',
      }),
    );

    const routes = isNonEmptyArray(steps)
      ? getRoutesFromStepper({
          steps,
          basePath: funnel.basePath,
          afterEndPath: funnel.afterEndPath,
          stepsI18nPrefix: `stepper.${funnel.name}`,
        })
      : [];

    return {
      routes: withFeatureFlag(routes, funnel.featureFlag),
      layout: funnel.layout ?? 'none',
      auth: funnel.auth,
      customLayout: funnel.customLayout,
    };
  });
}

export function computeProductCustomFunnelRoutes(
  productsConfig: Record<string, KorintModulesProductsConfig>,
) {
  return Object.entries(productsConfig).flatMap(([productId, config]) => {
    const customFunnels = config.customFunnels ?? [];

    return customFunnels.map(funnel => {
      const steps = funnel.steps.map(step =>
        mapConfigToStep(step, {
          productId,
          displayContext: funnel.auth === CustomFunnelAuth.ANONYMOUS ? 'policyholder' : 'broker',
        }),
      );
      const routes = isNonEmptyArray(steps)
        ? getRoutesFromStepper({
            steps,
            basePath: funnel.basePath,
            afterEndPath: funnel.afterEndPath,
            stepsI18nPrefix: `stepper.${productId}`,
          })
        : [];

      const landingRoute =
        funnel.auth === CustomFunnelAuth.ANONYMOUS
          ? [
              {
                path: funnel.basePath.split('/:customerId')[0],
                element: <AnonymousLandingPage basePath={funnel.basePath} productId={productId} />,
              },
            ]
          : [];

      return {
        routes: withFeatureFlag([...landingRoute, ...routes], funnel.featureFlag, productId),
        layout: funnel.layout ?? ('none' as const),
        auth: funnel.auth as CustomFunnelAuth | undefined,
        customLayout: funnel.customLayout,
      };
    });
  });
}
